import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FactCheck } from '@mui/icons-material';

import {
  useGetDevTestfunktion,
  useSaveDevTestfunktion,
} from '../../hooks/reactQuerys/DevZugangQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useAlert } from '../../context/AlertContext';
import { filterUsersBySearch, toggleUserId } from './userAccessList';

const FEATURE = 'gwgpruefung';

/**
 * GwG-Prüfung freischalten — unter /admin/dev/gwgpruefung.
 *
 * Testfunktion (`UserFeature`, Recht `gwgpruefung`): die GwG-Prüfung nach dem
 * BNotK-Fragenkatalog im Vorgang sehen nur die hier angehakten Personen,
 * alle anderen behalten die bisherige Platzhalterkarte. Anders als KI, Word
 * und Health Check ohne Hauptschalter — leer heißt niemand.
 */
export const GwGPruefungAccessOverview: React.FC = () => {
  const { showAlert } = useAlert();
  const { testfunktion, isLoadingTestfunktion } =
    useGetDevTestfunktion(FEATURE);
  const { saveTestfunktion, isPendingSaveTestfunktion } =
    useSaveDevTestfunktion(FEATURE);
  const { users, isLoadingUsers } = useGetUsers();

  const [enabledUserArray, setEnabledUserArray] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!testfunktion) {
      return;
    }

    setEnabledUserArray(testfunktion.EnabledUserArray ?? []);
  }, [testfunktion]);

  const visibleUsers = filterUsersBySearch(users ?? [], userSearch);

  async function handleSave() {
    try {
      await saveTestfunktion({ EnabledUserArray: enabledUserArray });
      showAlert({
        text: 'Die Freischaltung wurde gespeichert.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Die Freischaltung konnte nicht gespeichert werden.',
        severity: 'error',
      });
    }
  }

  if (isLoadingTestfunktion) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <FactCheck />
        <Typography variant='h5'>
          {testfunktion?.Label ?? 'GwG-Prüfung'}
        </Typography>
      </Stack>

      <Alert severity='info' sx={{ mb: 3 }}>
        Freigeschaltete Personen sehen im Vorgang die GwG-Prüfung nach dem
        BNotK-Fragenkatalog. Alle anderen behalten die bisherige Ansicht und
        bemerken davon nichts. Die Änderung wirkt beim nächsten Laden der Seite
        des betroffenen Benutzers.
      </Alert>

      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='subtitle2'>Freigeschaltete Benutzer</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Die GwG-Prüfung ist ausschließlich für die hier angehakten Personen
            sichtbar.
          </Typography>

          {isLoadingUsers ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size='small'
                  label='Benutzer suchen'
                  value={userSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setUserSearch(event.target.value)
                  }
                  sx={{ minWidth: 260 }}
                />
                <Typography variant='body2' color='text.secondary'>
                  {enabledUserArray.length} von {(users ?? []).length}{' '}
                  freigeschaltet
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                {visibleUsers.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ p: 1 }}
                  >
                    Kein Benutzer gefunden.
                  </Typography>
                ) : (
                  visibleUsers.map(user => (
                    <FormControlLabel
                      key={user.idUser}
                      sx={{ display: 'flex', mr: 0 }}
                      control={
                        <Checkbox
                          size='small'
                          checked={enabledUserArray.includes(user.idUser)}
                          onChange={() =>
                            setEnabledUserArray(previous =>
                              toggleUserId(previous, user.idUser)
                            )
                          }
                        />
                      }
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant='body2'>
                            {user.LastName}, {user.FirstName}
                          </Typography>
                          {Boolean(user.isNotary) && (
                            <Chip size='small' label='Notar' />
                          )}
                          <Typography variant='caption' color='text.secondary'>
                            {user.Email}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Button
        variant='contained'
        onClick={() => void handleSave()}
        disabled={isPendingSaveTestfunktion}
      >
        {isPendingSaveTestfunktion ? 'Wird gespeichert …' : 'Speichern'}
      </Button>
    </Box>
  );
};
